import { backendApi } from "./backend.api";
import { IVisit } from "shared/models/models";
import { getSpaceId } from "shared/utils/space";

interface listQuery {
  date?: string | Date;
  user?: number;
  addressId?: number;
  statusId?: number[];
  express?: boolean;
  spaceId?: number;
}

export interface MultiVisit {
  ids: string[];
  date: Date;
  express?: boolean;
  user: number;
  spaceId?: number;
}

export interface createFromRoute {
  date: string;
  routeId: number;
}

export const visitApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      visit: build.query<IVisit[], listQuery>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: listQuery = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/visit",
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Visit" as const, id })),
                "Visit",
              ]
            : ["Visit"],
      }),
      canFinish: build.query<boolean, string>({
        query: (id) => ({
          url: `/visit/can-finish/${id}`,
          method: "GET",
        }),
        keepUnusedDataFor: 5,
      }),
      getVisit: build.query<IVisit, string>({
        query: (id) => ({ url: `/visit/${id}`, method: "GET" }),
        transformErrorResponse: (response: { visit: string | number }) =>
          response.visit,
        providesTags: (item) => [{ type: "Visit", id: item?.id }],
      }),
      getNextVisit: build.query<IVisit[], string>({
        query: (id) => {
          const spaceId = getSpaceId();
          return {
            url: `/visit/next/${id}`,
            method: "GET",
            params: { spaceId: spaceId },
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Visit" as const, id })),
                "Visit",
              ]
            : ["Visit"],
      }),
      getPrevVisit: build.query<IVisit[], string>({
        query: (id) => {
          const spaceId = getSpaceId();
          return {
            url: `/visit/prev/${id}`,
            method: "GET",
            params: { spaceId: spaceId },
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Visit" as const, id })),
                "Visit",
              ]
            : ["Visit"],
      }),
      addVisit: build.mutation<IVisit, Partial<IVisit>>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: Partial<IVisit> = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/visit",
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: ["Visit", "VisitTaskCheck"],
        transformResponse: (response: { data: IVisit }) => response.data,
      }),
      addMultiVisits: build.mutation<IVisit[], MultiVisit>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: MultiVisit = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/visit/add-multi",
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: ["Visit", "VisitTaskCheck"],
        //transformResponse: (response: { data: IVisit[] }) => response.data,
      }),

      createFromRouteVisits: build.mutation<IVisit[], createFromRoute>({
        query: (payload) => {
          return {
            url: "/visit/create-from-route",
            method: "POST",
            data: payload,
          };
        },
        invalidatesTags: ["Visit", "VisitTaskCheck"],
        //transformResponse: (response: { data: IVisit[] }) => response.data,
      }),
      updateMultiVisits: build.mutation<IVisit[], IVisit[]>({
        query: (payload) => ({
          url: "/visit/update-multi",
          method: "POST",
          data: payload.map((v) => ({ id: v.id, sort: v.sort, date: v.date })),
        }),
        invalidatesTags: ["Visit", "VisitTaskCheck"],
        // invalidatesTags: (result = []) => [
        //   ...result.map(({ id }) => ({ type: "Visit", id: id }) as const),
        // ],
        // transformResponse: (response: { data: IVisit[] }) => response.data,
      }),
      updateVisit: build.mutation<IVisit, Partial<IVisit>>({
        query: (payload) => ({
          url: `/visit/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [{ type: "Visit", id: item?.id }],
        //transformResponse: (result, error) => [{ type: 'Visit', id: result?.id }],
      }),
      deleteVisit: build.mutation<IVisit, number>({
        query: (id) => ({
          url: `/visit/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: ["Visit", "VisitTaskCheck"],
      }),
    };
  },
});

export const {
  useVisitQuery,
  useGetNextVisitQuery,
  useGetPrevVisitQuery,
  useAddVisitMutation,
  useDeleteVisitMutation,
  useGetVisitQuery,
  useUpdateVisitMutation,
  useAddMultiVisitsMutation,
  useUpdateMultiVisitsMutation,
  useCreateFromRouteVisitsMutation,
  useCanFinishQuery,
} = visitApi;
